import { useMemo, useState } from 'react';

import {
  DifficultyLevel,
  IconButton,
  MultipleChoiceAssessmentAnswers,
  MultipleChoiceQuestionAnswer,
  MultipleChoiceQuestionAnswerType,
} from '@opositandopn/ui';
import classnames from 'classnames';
import {
  FcAcceptDatabase,
  FcAddDatabase,
  FcDataBackup,
  FcDeleteDatabase,
} from 'react-icons/fc';
import { IoChevronBackOutline, IoChevronForwardOutline } from 'react-icons/io5';

import { Loading } from '../../../../components/core/Loading';
import { ResourceDialog } from '../../../../components/shared/resource-dialog/ResourceDialog';
import { useIsPremium, useProfile } from '../../../../context/ProfileContext';
import { useAppActions } from '../../../../state/appStore';
import { BaseComponentProps } from '../../../../types/base-component.types';
import {
  useBookmarkMultipleChoiceQuestion,
  useCreateMultipleChoiceQuestionErrorReport,
  useUnbookmarkMultipleChoiceQuestion,
} from '../../../questions/api/useMultipleChoiceQuestions';
import { MultipleChoiceQuestionEditor } from '../../../questions/components/multiple-choice-question-editor/MultipleChoiceQuestionEditor';
import { ErrorReportForm } from '../../../reports/components/error-report-form/ErrorReportForm';
import {
  useExplainMultipleChoiceQuestionAttemptFromAssessment,
  useGetMultipleChoiceAssessmentAttempt,
  useGetMultipleChoiceAssessmentMetrics,
} from '../../api/useMultipleChoiceAssessments';
import {
  AssessmentScoreboard,
  hasContestedField,
} from '../assessment-scoreboard/AssessmentScoreboard';
import { MultipleChoiceAssessmentPreviewList } from '../multiple-choice-assessment-preview-list/MultipleChoiceAssessmentPreviewList';

import styles from './MultipleChoiceAssessmentAttemptResult.module.scss';

type ContainerProps = BaseComponentProps & {
  subjectId: string;
  assessmentAttemptId: string;
  onRetry?: (assessmentId: string) => void;
};

function MultipleChoiceAssessmentAttemptResult({
  className,
  subjectId,
  assessmentAttemptId,
  onRetry,
  testId = 'ui-multiple-choice-assessment-attempt-result',
}: ContainerProps) {
  const { profile } = useProfile();
  const { setShowGetPremiumPopup } = useAppActions();
  const isPremium = useIsPremium();

  const [isOpen, setIsOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const [bookmarks, setBookmarks] = useState<Record<string, boolean>>({});
  const [explanations, setExplanations] = useState<Record<string, string>>({});
  const [showEdit, setShowEdit] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showAssessments, setShowAssessments] = useState(false);

  const [shownAttemptId, setShownAttemptId] = useState(assessmentAttemptId);

  if (shownAttemptId !== assessmentAttemptId) {
    setShownAttemptId(assessmentAttemptId);
    setBookmarks({});
    setExplanations({});
    setIndex(0);
  }

  const { data: attemptResponse, isLoading: isAttemptLoading } =
    useGetMultipleChoiceAssessmentAttempt({
      subjectId,
      assessmentAttemptId,
    });

  const { data: metricsResponse } = useGetMultipleChoiceAssessmentMetrics(
    {
      subjectId,
      assessmentId: attemptResponse?.assessmentId as string,
    },
    attemptResponse?.assessmentId !== undefined,
  );

  const { mutateAsync: bookmarkMultipleChoiceQuestion } =
    useBookmarkMultipleChoiceQuestion();

  const { mutateAsync: unbookmarkMultipleChoiceQuestion } =
    useUnbookmarkMultipleChoiceQuestion();

  const { mutate: createMultipleChoiceQuestionErrorReport } =
    useCreateMultipleChoiceQuestionErrorReport();

  const { mutateAsync: explainMultipleChoiceQuestionAttemptFromAssessment } =
    useExplainMultipleChoiceQuestionAttemptFromAssessment();

  const questions = useMemo<MultipleChoiceQuestionAnswerType[]>(
    () =>
      (attemptResponse?.questionAttempts ?? []).map((questionAttempt) => ({
        id: questionAttempt.question.id,
        text: questionAttempt.question.text,
        subject:
          questionAttempt.question.lesson?.name ??
          questionAttempt.question.unit?.name ??
          questionAttempt.question.subject.name,
        caption:
          questionAttempt.question.status === 'removed'
            ? '(Pregunta eliminada)'
            : undefined,
        imageUrl: questionAttempt.question.imageUrl,
        bookmarked:
          bookmarks[questionAttempt.question.id] ??
          questionAttempt.question.bookmarked,
        explanation: explanations[questionAttempt.question.id],
        tags: questionAttempt.question.tags.map((tag) => tag.name),
        difficulty:
          questionAttempt.question.difficultyLevel === 'easy'
            ? DifficultyLevel.EASY
            : questionAttempt.question.difficultyLevel === 'medium'
              ? DifficultyLevel.MEDIUM
              : DifficultyLevel.HARD,
        options: questionAttempt.question.choices.map((choice) => choice.text),
        selectedOptionIndex:
          questionAttempt.answer === ''
            ? 0
            : 1 +
              questionAttempt.question.choices.findIndex(
                (choice) => choice.id === questionAttempt.answer,
              ),
        correctOptionIndex:
          1 +
          questionAttempt.question.choices.findIndex(
            (choice) => choice.isCorrect,
          ),
        reason: questionAttempt.question.explanation,
        answerRate: questionAttempt.question.metrics && {
          correct: questionAttempt.question.metrics.correctPercentage,
          skipped: questionAttempt.question.metrics.skippedPercentage,
          incorrect: questionAttempt.question.metrics.incorrectPercentage,
        },
        auditLog:
          profile.role === 'admin'
            ? questionAttempt.question.auditLog.map((log) => ({
                icon:
                  log.action === 'created' ? (
                    <FcAddDatabase />
                  ) : log.action === 'updated' ? (
                    <FcDataBackup />
                  ) : log.action === 'deleted' ? (
                    <FcDeleteDatabase />
                  ) : (
                    <FcAcceptDatabase />
                  ),
                title:
                  log.action === 'created'
                    ? 'Pregunta creada'
                    : log.action === 'updated'
                      ? 'Pregunta actualizada'
                      : log.action === 'deleted'
                        ? 'Pregunta eliminada'
                        : 'Pregunta verificada',

                description: `Por ${log.username}`,
                date: new Date(log.date),
              }))
            : undefined,
        tip:
          questionAttempt.question.missesCount > 1 ? (
            <div className={styles['tooltip']}>
              Has fallado esta pregunta {questionAttempt.question.missesCount}{' '}
              veces
            </div>
          ) : undefined,
      })),
    [attemptResponse, bookmarks, explanations, profile.role],
  );

  const question = questions[index];

  const currentQuestionAttempt = attemptResponse?.questionAttempts[index];
  const currentQuestion = currentQuestionAttempt?.question;
  const currentQuestionAttemptId = currentQuestionAttempt?.id;

  if (isAttemptLoading || !attemptResponse) {
    return <Loading />;
  }

  return (
    <div className={classnames(styles['main'], className)} data-testid={testId}>
      <MultipleChoiceAssessmentAnswers
        className={styles['result']}
        description={attemptResponse.name}
        score={attemptResponse.score}
        penalty={-attemptResponse.scorePenalty}
        elapsedTime={attemptResponse.elapsedTime}
        answerRate={{
          correct: attemptResponse.questionAttempts.filter(
            (questionAttempt) =>
              questionAttempt.answer ===
              questionAttempt.correction.correctAnswer,
          ).length,
          incorrect: attemptResponse.questionAttempts.filter(
            (questionAttempt) =>
              questionAttempt.answer !== '' &&
              questionAttempt.answer !==
                questionAttempt.correction.correctAnswer,
          ).length,
          skipped: attemptResponse.questionAttempts.filter(
            (questionAttempt) => questionAttempt.answer === '',
          ).length,
        }}
        questions={questions}
        official={attemptResponse.official}
        onSelect={(id: string) => {
          setIndex(questions.findIndex((q) => q.id === id));
          setIsOpen(true);
        }}
        onRetry={
          onRetry && isPremium && attemptResponse.status === 'published'
            ? () => {
                onRetry(attemptResponse.assessmentId);
              }
            : undefined
        }
        // The same board the home cards carry, so the standings and the marks
        // they are read against say one thing wherever they are read. Kept off
        // the card until a second mark makes a standing of it.
        scoreboard={
          metricsResponse &&
          hasContestedField(metricsResponse) && (
            <AssessmentScoreboard
              metrics={metricsResponse}
              ownUsername={profile.username}
              cutoffScore={attemptResponse.cutoffScore}
            />
          )
        }
      />
      <ResourceDialog open={isOpen}>
        <MultipleChoiceQuestionAnswer
          question={question}
          onBookmark={
            profile.subscription?.isActive &&
            currentQuestion?.status !== 'removed'
              ? async (id: string) => {
                  await bookmarkMultipleChoiceQuestion({
                    subjectId,
                    questionId: id,
                  });
                  setBookmarks((prevBookmarks) => ({
                    ...prevBookmarks,
                    [id]: true,
                  }));
                }
              : undefined
          }
          onUnbookmark={
            profile.subscription?.isActive
              ? async (id: string) => {
                  await unbookmarkMultipleChoiceQuestion({
                    subjectId,
                    questionId: id,
                  });
                  setBookmarks((prevBookmarks) => ({
                    ...prevBookmarks,
                    [id]: false,
                  }));
                }
              : undefined
          }
          onFeedback={
            isPremium && currentQuestion?.status !== 'removed'
              ? () => {
                  setShowFeedback(true);
                }
              : undefined
          }
          onExplain={
            currentQuestion?.status !== 'removed' && !currentQuestion?.imageUrl
              ? async () => {
                  if (!isPremium) {
                    setShowGetPremiumPopup(true);
                    throw new Error('Premium subscription required.');
                  }
                  if (question.explanation || !currentQuestionAttemptId) {
                    return;
                  }
                  const result =
                    await explainMultipleChoiceQuestionAttemptFromAssessment({
                      subjectId,
                      assessmentAttemptId,
                      questionAttemptId: currentQuestionAttemptId,
                    });
                  setExplanations((prevExplanations) => ({
                    ...prevExplanations,
                    [question.id]: result.explanation,
                  }));
                }
              : undefined
          }
          onClose={() => setIsOpen(false)}
        >
          <div className={styles['index']}>
            <IconButton
              className={styles['chevron']}
              size="xs"
              icon={<IoChevronBackOutline />}
              aria-label="Pregunta anterior"
              disabled={index === 0}
              onClick={() => setIndex(index - 1)}
            />
            <span className={styles['number']}>{index + 1}</span>
            <IconButton
              className={styles['chevron']}
              size="xs"
              icon={<IoChevronForwardOutline />}
              aria-label="Pregunta siguiente"
              disabled={index === questions.length - 1}
              onClick={() => setIndex(index + 1)}
            />
          </div>
        </MultipleChoiceQuestionAnswer>
        <MultipleChoiceQuestionEditor
          open={showEdit}
          question={currentQuestion!}
          onClose={() => setShowEdit(false)}
        />
        <MultipleChoiceAssessmentPreviewList
          open={showAssessments}
          question={currentQuestion!}
          onClose={() => setShowAssessments(false)}
        />
        <ErrorReportForm
          open={showFeedback}
          title="¿Has visto algún error en esta pregunta?"
          onFeedback={async (feedback) => {
            createMultipleChoiceQuestionErrorReport(
              {
                subjectId,
                questionId: question.id,
                message: feedback,
              },
              {
                onSuccess: () => {
                  setShowFeedback(false);
                },
              },
            );
          }}
          onClose={() => setShowFeedback(false)}
        />
      </ResourceDialog>
    </div>
  );
}

export { MultipleChoiceAssessmentAttemptResult };
